/**
 * Transient live-mode runtime state. These values must never be persisted in the
 * localStorage conversation index (see Phase 2 `StoredConversationIndex`).
 */

export type ConversationLoadState = 'loading' | 'ready' | 'unavailable';

export type SendBlockReason =
  | 'pending'
  | 'delayed'
  | 'full'
  | 'cooldown'
  | 'unavailable';

/** Compatibility fallback while rolling out config responses with this field. */
export const DEFAULT_CHAT_MESSAGE_MAX_LENGTH = 4_000;

/** Milliseconds to disable repeat submission after a `429 rate_limited` response. */
export const SEND_COOLDOWN_MS = 5_000;

/** Interval between successful assistant status polls (Phase 4). */
export const POLL_INTERVAL_MS = 2_000;

/**
 * Stop background guardrail settlement checks after this window.
 * Matches backend default: min((GUARDRAIL_SETTLE_TIMEOUT_SECONDS + 5) × 1000, POLL_INTERVAL_MS × 120).
 */
export const SETTLE_POLL_MAX_MS = 95_000;

/** Pending age after which the UI shows the delayed state (~120 s). */
export const POLL_DELAYED_THRESHOLD_MS = 120_000;

/**
 * Pending age after which polling stops with `stuck` (~310 s).
 * Sits above the backend `CHATBOT_PENDING_TIMEOUT_SECONDS` default of 300 s.
 */
export const POLL_STUCK_THRESHOLD_MS = 310_000;

/** Initial retry delay for transient poll failures; doubles up to the max. */
export const POLL_RETRY_BACKOFF_INITIAL_MS = 2_000;

/** Maximum retry delay for transient poll failures. */
export const POLL_RETRY_BACKOFF_MAX_MS = 10_000;

/** Danish hints shown when sending is blocked for a recoverable reason. */
export const SEND_BLOCK_HINTS_DA: Record<SendBlockReason, string> = {
  pending: 'Der genereres allerede et svar',
  delayed: 'Svaret tager længere tid end normalt',
  full: 'Denne chat har nået beskedgrænsen',
  cooldown: 'For mange forespørgsler - prøv igen om lidt',
  unavailable: 'GymGPT er midlertidigt utilgængelig. Prøv igen.',
};
